import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackSendChatbotMessage } from './tracking';
import { setHintQuestionsHidden } from '../../actions/chatHintQuestions';
import { sendFirstMessageAgent, sendFirstMessageCustomChat, sendFirstMessageOffer } from '../../actions/firstMessage';
import { trackFirstMessageSent, trackFirstMessageWithBotSent } from '../../actions/firstMessage/tracking';
import { sendMessage } from '../../actions/sendMessage';
import { BLOB_ID } from '../../constants/blobId';
import { CHATBOT_ID } from '../../constants/chatbotId';
import {
  agentIdSelector,
  customChatParamsSelector,
  currentOpenChatSelector,
  offerSelector,
  parentSourceSelector,
  userSelector,
} from '../../selectors';
import { HISTORY_MESSAGES_REQUEST_LIMIT } from '../../services/fetchHistoryMessages';
import { saveChatMessageHint } from '../../services/saveChatMessageHint';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { TAppState } from '../../types/appState';
import { TAnalyticsKey } from '../../types/chatHint';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { useApplicationContext } from '../../utils/applicationContext';
import { isSendUserModifiedSuggestionsEnabled } from '../../utils/isSendUserModifiedSuggestionsEnabled';

export function useSendMessage(isAnalyticsKeyDisabled = false) {
  const dispatch = useDispatch();

  const offer = useSelector(offerSelector);
  const agentId = useSelector(agentIdSelector);
  const customChatParams = useSelector(customChatParamsSelector);
  const currentChat = useSelector(currentOpenChatSelector);
  const leadUser = useSelector(userSelector);
  const parentSource = useSelector(parentSourceSelector);
  const isEarlyAccess = useSelector((state: TAppState) => state.isEarlyAccess);

  const { captchaToken, firstMessageStatus, chatId, chatMessages, ga, users, botId } =
    currentChat || /* istanbul ignore next */ {};

  const { config } = useApplicationContext();

  const [analyticsKey, setAnalyticsKey] = React.useState<TAnalyticsKey | null>(null);
  const [savedPayload, setSavedPayload] = React.useState<string | undefined>();
  const [savedText, setSavedText] = React.useState<string>('');

  const { id: offerId } = offer || {};

  const isSendModifiedSuggestionsEnabled = isSendUserModifiedSuggestionsEnabled(config);
  const cianConsultantUserId = config.get<number>('frontend-chats.cianConsultantUserId');
  const cianConsultantBotId = config.get<number>('frontend-chats.cianConsultantBotId');

  const onSend = React.useCallback(
    ({ text, chatId, payload }: { text: string; chatId: string; payload?: string }) => {
      if (offer && analyticsKey && text && isSendModifiedSuggestionsEnabled) {
        dispatch(saveChatMessageHint({ offer, analyticsKey, messageText: text }));
      }
      dispatch(setHintQuestionsHidden({ chatId }));

      if (chatId === BLOB_ID && offerId) {
        setSavedText(text);
        setSavedPayload(payload);

        dispatch(sendFirstMessageOffer({ text, offerId, captchaToken, payload }));

        return;
      }

      // TODO: Добавить кейс отправки сообщения агенту
      /* istanbul ignore next */
      if (chatId === BLOB_ID && agentId) {
        dispatch(sendFirstMessageAgent({ text, captchaToken, agentId }));

        return;
      }

      if (chatId === BLOB_ID && customChatParams) {
        dispatch(sendFirstMessageCustomChat({ text, captchaToken, customChatParams }));

        return;
      }

      if (chatId === CHATBOT_ID) {
        trackSendChatbotMessage(text);
        dispatch(sendChatbotMessage(text));

        return;
      }
      const chatMessagesLength = chatMessages?.length || 0;

      // TODO: убрать эту аналитику и использовать универсальное решение с trackFirstMessageSentByInitiator (сейчас поля в событии не совпадают)
      if (chatId.includes(`${cianConsultantUserId}`) && chatMessagesLength <= 2) {
        const initiator = users?.find(user => user.chatRole === 'initiator');
        // Для чата с КпН отправляем событие первого сообщения отдельно
        trackFirstMessageWithBotSent({
          botId: cianConsultantBotId,
          label: ga?.source,
          ownerId: initiator?.userId,
          opponentId: leadUser.userId,
          analyticsKey: isAnalyticsKeyDisabled ? null : analyticsKey,
          pageTypeSource: parentSource,
        });
      }

      // может быть ситуация, когда чат уже создан (chatId !== blob), но сообщений от пользователя еще не было, например чат с рассылкой по объявлению
      // в этом случае, если чат уже есть, но пользователь отправляет сообщение первый раз - логгируем отправку первого сообщения
      // для проверки берём первый запрос за сообщениями в чате с лимитом HISTORY_MESSAGES_REQUEST_LIMIT
      // TODO: удалить проверку на chatId после удаления кода КпН
      if (
        chatMessages &&
        chatMessagesLength &&
        chatMessagesLength < HISTORY_MESSAGES_REQUEST_LIMIT &&
        !chatId.includes(`${cianConsultantUserId}`)
      ) {
        // если чат с чатботом, то поискун имеет роль оппонента, а бот имеет роль инициатора (а должен компаньона!, баг на беке)
        // если чат не с чатботом, то поискун имеет роль инициатора
        const initiator = botId
          ? users?.find(user => user.chatRole === 'opponent')
          : users?.find(user => user.chatRole === 'initiator');

        // если это первое сообщение в чате от текущего пользователя инициатора
        if (
          initiator?.userId === leadUser.userId &&
          chatMessages.filter(message => message.message.userId === leadUser.userId).length === 0
        ) {
          trackFirstMessageSent({
            ga,
            users,
            offerId,
            agentId,
            userId: leadUser.userId,
            analyticsKey: isAnalyticsKeyDisabled ? null : analyticsKey,
            pageTypeSource: parentSource,
            isEarlyAccess,
            chatId,
          });
        }
      }

      dispatch(sendMessage({ text, chatId, payload }));
    },
    [
      offer,
      analyticsKey,
      isSendModifiedSuggestionsEnabled,
      dispatch,
      offerId,
      agentId,
      chatMessages,
      customChatParams,
      cianConsultantUserId,
      captchaToken,
      isAnalyticsKeyDisabled,
      users,
      cianConsultantBotId,
      ga,
      leadUser.userId,
      parentSource,
      botId,
      isEarlyAccess,
    ],
  );

  React.useEffect(() => {
    // TODO: Придумать кейс с капчей и добавить
    /* istanbul ignore next */
    if (chatId && savedPayload && firstMessageStatus === EFirstMessageStatus.NeedCaptcha && captchaToken) {
      onSend({ text: savedText, chatId, payload: savedPayload });
    }
  }, [captchaToken, chatId, firstMessageStatus, onSend, savedPayload, savedText]);

  return React.useMemo(() => ({ onSend, setAnalyticsKey }), [onSend]);
}
